import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography, Button, Alert, Chip, CircularProgress, Stack, Select, MenuItem,
  FormControl, InputLabel, TextField,
} from '@mui/material';
import { colorTaxonomyApi } from '../../services/api';

export default function ColorTypesPanel() {
  const [types, setTypes] = useState([]);
  const [families, setFamilies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [filterFamily, setFilterFamily] = useState('');
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [typesResp, famResp] = await Promise.all([
        colorTaxonomyApi.listTypes(),
        colorTaxonomyApi.listFamilies(),
      ]);
      setTypes(typesResp.types || []);
      setFamilies(famResp.families || []);
    } catch (e) {
      setError(e.message || 'Failed to load colors');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleReassign = async (normalizedValue, family) => {
    try {
      setError(null);
      setInfo(null);
      const resp = await colorTaxonomyApi.updateTypeFamily(normalizedValue, family);
      const updated = resp.result?.rows_updated ?? 0;
      setInfo(`Reassigned "${normalizedValue}" to family "${family}" (${updated} rows updated)`);
      await load();
    } catch (e) {
      setError(e.message || 'Failed to reassign');
    }
  };

  const filtered = types.filter(t => {
    if (filterFamily && t.family !== filterFamily) return false;
    if (search && !t.normalized_value.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {info && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setInfo(null)}>{info}</Alert>}

      <Stack direction="row" spacing={2} sx={{ mb: 2, alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Family</InputLabel>
          <Select value={filterFamily} label="Family" onChange={(e) => setFilterFamily(e.target.value)}>
            <MenuItem value=""><em>All</em></MenuItem>
            {families.map(f => <MenuItem key={f.id} value={f.family}>{f.family}</MenuItem>)}
          </Select>
        </FormControl>
        <TextField size="small" label="Color contains..." value={search} onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: 240 }} />
        {(filterFamily || search) && <Button size="small" onClick={() => { setFilterFamily(''); setSearch(''); }}>Clear</Button>}
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" color="text.secondary">
          {filtered.length} of {types.length} normalized colors
        </Typography>
      </Stack>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell><strong>Normalized Color</strong></TableCell>
              <TableCell align="right" width={120}><strong>Mapping rows</strong></TableCell>
              <TableCell width={260}><strong>Family</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map(t => (
              <TableRow key={t.normalized_value}>
                <TableCell><Chip label={t.normalized_value} size="small" variant="outlined" /></TableCell>
                <TableCell align="right">{t.row_count}</TableCell>
                <TableCell>
                  <FormControl size="small" fullWidth>
                    <Select
                      value={t.family || ''}
                      onChange={(e) => handleReassign(t.normalized_value, e.target.value)}
                      displayEmpty
                    >
                      {!t.family && <MenuItem value=""><em>(unassigned)</em></MenuItem>}
                      {families.map(f => <MenuItem key={f.id} value={f.family}>{f.family}</MenuItem>)}
                    </Select>
                  </FormControl>
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={3} align="center"><Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No colors match filters.</Typography></TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
